import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { withTxToast, txToast } from '../utils/txToast';
import { Package, Loader, RefreshCw, Tag, ExternalLink, Download } from 'lucide-react';

const isPdfUrl = (url = '') => {
  const noQuery = String(url).split('?')[0].toLowerCase();
  return noQuery.endsWith('.pdf');
};

const resolveMediaFromMetadata = (uri, metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return { mediaUrl: '', mediaType: 'unknown', title: '' };
  }

  const mediaUrl = metadata.image || metadata.animation_url || metadata.external_url || '';
  const mediaType = isPdfUrl(mediaUrl) ? 'pdf' : mediaUrl ? 'image' : 'unknown';
  const title = metadata.name || '';

  return { mediaUrl, mediaType, title };
};

const MyNFTs = ({ nftContract, marketplaceContract, nftContractAddress, marketplaceContractAddress, refreshSignal, onRefresh }) => {
  const { web3, account } = useWeb3();
  const { t } = useLanguage();
  const [myNFTs, setMyNFTs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listingPrice, setListingPrice] = useState({});
  const [listingNFT, setListingNFT] = useState(null);

  useEffect(() => {
    loadMyNFTs();
  }, [nftContract, account, refreshSignal]);

  const loadMyNFTs = async () => {
    if (!nftContract || !account || !web3) return;

    setLoading(true);
    try {
      // Use ERC721Enumerable: only query tokens owned by this account
      const balance = Number(await nftContract.methods.balanceOf(account).call());
      if (balance === 0) {
        setMyNFTs([]);
        return;
      }

      const tokenIds = await Promise.all(
        Array.from({ length: balance }, (_, i) =>
          nftContract.methods.tokenOfOwnerByIndex(account, i).call()
        )
      );

      const settled = await Promise.allSettled(
        tokenIds.map(async (tokenId) => {
          try {
            const [uri, creator, isListed] = await Promise.all([
              nftContract.methods.tokenURI(tokenId).call(),
              nftContract.methods.getTokenCreator(tokenId).call(),
              marketplaceContract
                ? marketplaceContract.methods.isListed(nftContractAddress, tokenId).call()
                : Promise.resolve(false),
            ]);

            let metadata = null;
            try {
              const res = await fetch(uri, { cache: 'no-store' });
              if (res.ok) metadata = await res.json();
            } catch {
              // Ignore metadata fetch errors and keep rendering tokenURI details.
            }

            const { mediaUrl, mediaType, title } = resolveMediaFromMetadata(uri, metadata);

            return { tokenId, uri, creator, isListed, metadata, mediaUrl, mediaType, title };
          } catch {
            return null;
          }
        })
      );

      setMyNFTs(settled.filter(r => r.status === 'fulfilled' && r.value !== null).map(r => r.value));
    } catch (err) {
      console.error('Error loading NFTs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportToMetaMask = async (tokenId) => {
    if (!window.ethereum) {
      txToast.info(t('myNFTs.errNoMetaMask'));
      return;
    }
    try {
      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC721',
          options: {
            address: nftContractAddress,
            tokenId: String(tokenId),
          },
        },
      });
    } catch (err) {
      txToast.info(t('myNFTs.errImport', { message: err?.message || '' }));
    }
  };

  const handleListNFT = async (tokenId) => {
    if (!marketplaceContract || !nftContract || !account || !web3) return;

    const price = listingPrice[tokenId];
    if (!price || parseFloat(price) <= 0) {
      txToast.info(t('myNFTs.errInvalidPrice'));
      return;
    }

    setListingNFT(tokenId);
    try {
      // Paso 1/2: Aprobar el marketplace como operador (solo si no está ya aprobado)
      const alreadyApproved = await nftContract.methods.isApprovedForAll(account, marketplaceContractAddress).call();
      if (!alreadyApproved) {
        await withTxToast(
          () => nftContract.methods.setApprovalForAll(marketplaceContractAddress, true).send({ from: account, gas: 60000 }),
          t('myNFTs.step1Loading'),
          t('myNFTs.step1Success')
        );
      }

      // Paso 2/2: Registrar el NFT en el marketplace
      const priceWei = web3.utils.toWei(price, 'ether');
      await withTxToast(
        () => marketplaceContract.methods.listNFT(nftContractAddress, tokenId, priceWei).send({ from: account, gas: 200000 }),
        t('myNFTs.step2Loading'),
        t('myNFTs.step2Success')
      );

      await loadMyNFTs();
      onRefresh?.();
    } catch (err) {
      // error already shown by withTxToast
    } finally {
      setListingNFT(null);
    }
  };

  if (!account) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Package className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('myNFTs.title')}</h2>
        </div>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('myNFTs.noWallet')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Package className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('myNFTs.title')}</h2>
        </div>
        <button
          onClick={loadMyNFTs}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : myNFTs.length === 0 ? (
        <div className="p-8 text-center bg-gray-50 rounded-lg">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">{t('myNFTs.empty')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('myNFTs.emptySubtext')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {myNFTs.map((nft) => (
            <div key={nft.tokenId} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{nft.title || `NFT #${nft.tokenId}`}</h3>
                  <p className="text-sm text-purple-700 font-semibold">Token ID: {nft.tokenId}</p>
                  <p className="text-xs text-gray-500">
                    Creator: {nft.creator.substring(0, 6)}...{nft.creator.substring(38)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {nft.isListed && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                      {t('myNFTs.listed')}
                    </span>
                  )}
                  <button
                    onClick={() => handleImportToMetaMask(nft.tokenId)}
                    title={t('myNFTs.importBtn')}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-50 text-orange-600 border border-orange-200 rounded hover:bg-orange-100 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    <span>{t('myNFTs.importBtn')}</span>
                  </button>
                </div>
              </div>

              <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-2">
                {nft.mediaUrl ? (
                  nft.mediaType === 'pdf' ? (
                    <a
                      href={nft.mediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-700 hover:underline"
                    >
                      Ver archivo PDF del NFT
                    </a>
                  ) : (
                    <a href={nft.mediaUrl} target="_blank" rel="noopener noreferrer">
                      <img
                        src={nft.mediaUrl}
                        alt={nft.title || `NFT ${nft.tokenId}`}
                        className="w-full h-44 object-cover rounded"
                      />
                    </a>
                  )
                ) : (
                  <p className="text-xs text-gray-500">No se pudo cargar la imagen/archivo desde metadata.</p>
                )}
              </div>

              <div className="mb-3">
                <p className="text-xs text-gray-600 mb-1">Token URI:</p>
                <a
                  href={nft.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center space-x-1"
                >
                  <span className="truncate">{nft.uri.substring(0, 30)}...</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </div>

              {!nft.isListed && marketplaceContract && (
                <div className="space-y-2">
                  <input
                    type="number"
                    placeholder={t('myNFTs.pricePlaceholder')}
                    step="0.001"
                    min="0"
                    value={listingPrice[nft.tokenId] || ''}
                    onChange={(e) => setListingPrice({ ...listingPrice, [nft.tokenId]: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={listingNFT === nft.tokenId}
                  />
                  <button
                    onClick={() => handleListNFT(nft.tokenId)}
                    disabled={listingNFT === nft.tokenId}
                    className="w-full btn-primary text-sm py-2 flex items-center justify-center space-x-2"
                  >
                    {listingNFT === nft.tokenId ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                      <span>{t('myNFTs.listingBtn')}</span>
                      </>
                    ) : (
                      <>
                        <Tag className="w-4 h-4" />
                        <span>{t('myNFTs.listBtn')}</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyNFTs;
